import * as amplitude from "@amplitude/analytics-react-native"
import React, { useEffect, useState } from "react"
import { Image, Linking, Pressable, ScrollView, View } from "react-native"
import { ModalM } from "@common/components/modal/ModalM"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import IconButtonM from "@common/components/button/IconButtonM"
import { theme } from "@common/theme/theme"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { usePermissions } from "@common/livekit/components/PermissionBanner"
import { useMicrophoneLevel } from "../../engine/precall/useMicrophoneLevel"
import { useMicSilentDetection } from "@common/components/microphone/useMicSilentDetection"
import MicrophoneLevelBars from "@common/components/microphone/MicrophoneLevelBars"
import {
    useConnectionQualityIndicator,
    useLocalParticipant,
    useMaybeRoomContext,
} from "@livekit/components-react"
import { ConnectionQuality, Track } from "livekit-client"
import { IS_WEB } from "@common/utils/mobileUtils"
import { MediaDeviceSelectM } from "@common/livekit/components/media-device/MediaDeviceSelectM"
import micMutePc from "@assets/mic-mute-pc.jpeg"
import guidancePermissionWeb from "@assets/guidance-permission-web.png"

// ---------------------------------------------------------------------------
//  Constants
// ---------------------------------------------------------------------------

export const PROBLEMS = {
    MIC_NOT_WORKING: "mic_not_working",
    CANT_HEAR: "cant_hear",
    CAMERA_NOT_WORKING: "camera_not_working",
    CONNECTION_ISSUES: "connection_issues",
    OTHER: "other",
}

const PROBLEM_OPTIONS = [
    {
        key: PROBLEMS.MIC_NOT_WORKING,
        icon: "microphone-slash",
        label: "On ne m'entend pas",
        tutorLabel: "Les élèves ne m'entendent pas",
        color: theme.colors.error,
    },
    {
        key: PROBLEMS.CANT_HEAR,
        icon: "volume-mute",
        label: "Je n'entends pas mon tuteur",
        tutorLabel: "Je n'entends pas les élèves",
        color: theme.colors.warning,
    },
    {
        key: PROBLEMS.CAMERA_NOT_WORKING,
        icon: "video-slash",
        label: "Ma caméra ne marche pas",
        color: theme.colors.error,
    },
    {
        key: PROBLEMS.CONNECTION_ISSUES,
        icon: "wifi",
        label: "Ça rame / ça coupe",
        color: theme.colors.warning,
    },
    {
        key: PROBLEMS.OTHER,
        icon: "question-circle",
        label: "Autre problème",
        color: theme.colors.grey,
    },
]

const CONNECTION_LABELS = {
    [ConnectionQuality.Excellent]: {
        label: "Excellente",
        color: theme.colors.success,
    },
    [ConnectionQuality.Good]: { label: "Bonne", color: theme.colors.success },
    [ConnectionQuality.Poor]: { label: "Faible", color: theme.colors.warning },
    [ConnectionQuality.Lost]: { label: "Perdue", color: theme.colors.error },
    [ConnectionQuality.Unknown]: {
        label: "Inconnue",
        color: theme.colors.grey,
    },
}

// ---------------------------------------------------------------------------
//  Reusable sub-components
// ---------------------------------------------------------------------------

const Step = ({ number, text, subText }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "flex-start",
            marginBottom: 12,
            gap: 10,
        }}
    >
        <View
            style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: theme.colors.primaryLighter,
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <TextM fontSize={11} fontWeight="Bold" color={theme.colors.primary}>
                {number}
            </TextM>
        </View>
        <View style={{ flex: 1 }}>
            <TextM fontSize={13} wrap>
                {text}
            </TextM>
            {subText ? (
                <TextM
                    fontSize={11}
                    color={theme.colors.grey}
                    wrap
                    style={{ marginTop: 2 }}
                >
                    {subText}
                </TextM>
            ) : null}
        </View>
    </View>
)

const QuickAction = ({
    icon,
    label,
    onPress,
    color = theme.colors.primary,
}) => (
    <Pressable
        onPress={onPress}
        style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            backgroundColor: color + "12",
            paddingHorizontal: 12,
            paddingVertical: 9,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: color + "30",
        }}
    >
        <FontAwesome5 name={icon} size={13} color={color} />
        <TextM fontSize={12} fontWeight="SemiBold" color={color}>
            {label}
        </TextM>
    </Pressable>
)

const StatusBanner = ({ icon, text, color }) => (
    <View
        style={{
            backgroundColor: color + "15",
            padding: 12,
            borderRadius: 10,
            marginBottom: 12,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
        }}
    >
        <FontAwesome5 name={icon} size={14} color={color} />
        <TextM
            fontSize={12}
            color={color}
            fontWeight="SemiBold"
            wrap
            style={{ flex: 1 }}
        >
            {text}
        </TextM>
    </View>
)

const SectionTitle = ({ children }) => (
    <TextM
        fontWeight="SemiBold"
        fontSize={12}
        color={theme.colors.primary}
        style={{ marginBottom: 8 }}
    >
        {children}
    </TextM>
)

/**
 * On web: "J'ai autorisé" button that re-checks permission via getUserMedia.
 * If granted → updates state. If still denied → morphs into "Rafraîchir la page".
 * On mobile: opens system settings directly.
 */
const PermissionAction = ({ kind = "both", onRequestPermission }) => {
    const [denied, setDenied] = useState(false)

    if (!IS_WEB) {
        return (
            <QuickAction
                icon="cog"
                label="Ouvrir les paramètres"
                onPress={() => Linking.openSettings()}
            />
        )
    }

    if (denied) {
        return (
            <View style={{ gap: 6, width: "100%" }}>
                <TextM fontSize={11} color={theme.colors.error} wrap>
                    Permission non détectée. Clique sur le bouton à gauche de la barre d'adresse pour autoriser.
                </TextM>
                <View style={{ flexDirection: "row" }}>
                    <QuickAction
                        icon="redo"
                        label="Rafraîchir la page"
                        onPress={() => window.location.reload()}
                    />
                </View>
            </View>
        )
    }

    const labelText = kind === "camera"
        ? "J'ai autorisé la caméra"
        : kind === "microphone"
        ? "J'ai autorisé le micro"
        : "J'ai donné les autorisations"

    return (
        <QuickAction
            icon="shield-alt"
            label={labelText}
            color={theme.colors.success}
            onPress={async () => {
                try {
                    const constraints = kind === "camera"
                        ? { video: true }
                        : kind === "microphone"
                        ? { audio: true }
                        : { audio: true, video: true }
                    const stream = await navigator.mediaDevices.getUserMedia(constraints)
                    stream.getTracks().forEach((t) => t.stop())
                    onRequestPermission?.()
                } catch {
                    setDenied(true)
                }
            }}
        />
    )
}

/**
 * Opens the device selector modal (MediaDeviceSelectM) from within the
 * troubleshooting panel.  Web-only — on native, device switching for
 * mic/camera is not supported so the button is not rendered.
 */
const DeviceChangeButton = ({ kind, isSoundMode }) => {
    const [isOpen, setIsOpen] = useState(false)
    const room = useMaybeRoomContext()

    const track = React.useMemo(() => {
        if (!room) return undefined
        const participant = room.localParticipant
        if (kind === "audioinput") {
            return participant.getTrackPublication(Track.Source.Microphone)
                ?.audioTrack
        } else if (kind === "videoinput") {
            return participant.getTrackPublication(Track.Source.Camera)
                ?.videoTrack
        }
        return undefined
    }, [room, kind])

    if (!IS_WEB) return null

    const label =
        kind === "videoinput" ? "Changer de caméra" : "Changer de micro"
    const icon = kind === "videoinput" ? "video" : "microphone"

    return (
        <>
            <QuickAction
                icon={icon}
                label={label}
                onPress={() => setIsOpen(true)}
            />
            <MediaDeviceSelectM
                kind={kind}
                track={track}
                requestPermissions={true}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                includeAudioOutput={kind === "audioinput"}
                isSoundMode={isSoundMode}
            />
        </>
    )
}

const EscalationSection = ({
    onRaiseHand,
    onOpenChat,
    onOpenReport,
    showRaiseHand = true,
}) => {
    const hasAnyAction =
        (showRaiseHand && onRaiseHand) || onOpenChat || onOpenReport
    if (!hasAnyAction) return null

    return (
        <View
            style={{
                backgroundColor: theme.colors.lighter,
                borderRadius: 12,
                padding: 16,
                marginTop: 16,
            }}
        >
            <TextM fontWeight="Bold" fontSize={13} style={{ marginBottom: 12 }}>
                TOUJOURS UN PROBLÈME ?
            </TextM>
            <View
                style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}
            >
                {showRaiseHand && onRaiseHand && (
                    <QuickAction
                        icon="hand-paper"
                        label="Lever la main"
                        onPress={onRaiseHand}
                    />
                )}
                {onOpenChat && (
                    <QuickAction
                        icon="comment"
                        label="Écrire au tuteur"
                        onPress={onOpenChat}
                    />
                )}
                {onOpenReport && (
                    <QuickAction
                        icon="flag"
                        label="Signaler un problème"
                        onPress={onOpenReport}
                        color={theme.colors.error}
                    />
                )}
            </View>
        </View>
    )
}

// ---------------------------------------------------------------------------
//  Step 1: Problem Selection
// ---------------------------------------------------------------------------

const ProblemSelector = ({ onSelectProblem, role }) => (
    <View style={{ gap: 10 }}>
        <TextM
            fontSize={14}
            color={theme.colors.grey}
            style={{ marginBottom: 4 }}
            wrap
        >
            Sélectionne le problème que tu rencontres pour qu'on t'aide à le
            résoudre.
        </TextM>
        {PROBLEM_OPTIONS.map((option) => (
            <Pressable
                key={option.key}
                onPress={() => onSelectProblem(option.key)}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 14,
                    backgroundColor: theme.colors.white,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: theme.colors.lightMore,
                    padding: 16,
                }}
            >
                <View
                    style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: option.color + "15",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <FontAwesome5
                        name={option.icon}
                        size={16}
                        color={option.color}
                    />
                </View>
                <TextM
                    fontWeight="SemiBold"
                    fontSize={14}
                    style={{ flex: 1 }}
                    wrap
                >
                    {role === "tutor" && option.tutorLabel
                        ? option.tutorLabel
                        : option.label}
                </TextM>
                <FontAwesome5
                    name="chevron-right"
                    size={12}
                    color={theme.colors.grey}
                />
            </Pressable>
        ))}
    </View>
)

// ---------------------------------------------------------------------------
//  Step 2: Diagnostic views per problem
// ---------------------------------------------------------------------------

const MicDiagnostic = ({
    isSoundMode,
    onRaiseHand,
    onOpenChat,
    onOpenReport,
    parentMicLevel,
    parentPermissions,
    parentRequestPermissions,
}) => {
    // Use parent-provided values to avoid double recording (crash on Android)
    // Fallback to own hooks only when parent doesn't provide them
    const ownPermissions = usePermissions()
    const permissionsGranted = parentPermissions || ownPermissions.permissionsGranted
    const requestPermissions = parentRequestPermissions || ownPermissions.requestPermissions
    const ownMicLevel = useMicrophoneLevel(parentMicLevel !== undefined ? null : permissionsGranted)
    const micLevel = parentMicLevel !== undefined ? parentMicLevel : ownMicLevel
    const { isMicrophoneEnabled } = useLocalParticipant()

    const isMicPermissionOk = permissionsGranted?.microphone
    const isMicEnabled = isMicrophoneEnabled
    const micShouldWork =
        isMicPermissionOk && isMicEnabled !== false && isSoundMode !== false
    const isMicSilent = useMicSilentDetection(micLevel, micShouldWork)
    const isMicOk = micShouldWork && !isMicSilent

    return (
        <View>
            {/* Auto-diagnostic result */}
            {isMicOk ? (
                <StatusBanner
                    icon="check-circle"
                    text="Ton micro semble fonctionner correctement. Si le problème persiste, suis les étapes ci-dessous."
                    color={theme.colors.success}
                />
            ) : isSoundMode === false ? (
                <>
                    <StatusBanner
                        icon="volume-mute"
                        text="Tu es en mode Concentré — le son est coupé, c'est normal. Lève la main ou écris un message pour demander au tuteur de te remettre en mode Dialogue."
                        color={theme.colors.purple}
                    />
                    <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                        {onRaiseHand && (
                            <QuickAction
                                icon="hand-paper"
                                label="Lever la main"
                                onPress={onRaiseHand}
                            />
                        )}
                        {onOpenChat && (
                            <QuickAction
                                icon="comment"
                                label="Écrire au tuteur"
                                onPress={onOpenChat}
                            />
                        )}
                    </View>
                </>
            ) : !isMicPermissionOk ? (
                <>
                    <StatusBanner
                        icon="shield-alt"
                        text={
                            IS_WEB
                                ? "Le micro n'est pas autorisé dans ton navigateur. Clique sur le bouton à gauche de la barre d'adresse, autorise le micro, puis rafraîchis la page."
                                : "Le micro n'est pas autorisé. Ouvre les paramètres de l'application pour l'activer."
                        }
                        color={theme.colors.error}
                    />
                    {IS_WEB && (
                        <Image
                            source={guidancePermissionWeb}
                            style={{ width: "100%", height: 40, borderRadius: 8, marginBottom: 12 }}
                            resizeMode="contain"
                        />
                    )}
                </>
            ) : isMicEnabled === false ? (
                <StatusBanner
                    icon="microphone-slash"
                    text="Ton micro est coupé dans l'application. Clique sur le bouton micro en bas de l'écran pour le réactiver."
                    color={theme.colors.warning}
                />
            ) : isMicSilent ? (
                <StatusBanner
                    icon="keyboard"
                    text="Ton micro semble muet au niveau du système. Vérifie le bouton mute de ton clavier ou les paramètres son de ton appareil."
                    color={theme.colors.error}
                />
            ) : null}

            {/* When permissions denied: show only permission fix flow */}
            {!isMicPermissionOk && (
                <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <PermissionAction
                        kind="microphone"
                        onRequestPermission={requestPermissions}
                    />
                </View>
            )}

            {/* When permissions OK: show mic test + troubleshooting steps */}
            {isMicPermissionOk && (
                <>
                    {/* Mic test */}
                    <View
                        style={{
                            backgroundColor: theme.colors.lighter,
                            padding: 12,
                            borderRadius: 10,
                            marginBottom: 12,
                            alignItems: "center",
                        }}
                    >
                        <TextM
                            fontWeight="SemiBold"
                            fontSize={12}
                            style={{ marginBottom: 8 }}
                        >
                            Teste ton micro :
                        </TextM>
                        <MicrophoneLevelBars micLevel={micLevel} />
                        <TextM
                            fontSize={9}
                            color={theme.colors.grey}
                            style={{ marginTop: 8, textAlign: "center" }}
                            wrap
                        >
                            Si les points s'allument quand tu parles, ton micro
                            fonctionne !
                        </TextM>
                    </View>

                    <SectionTitle>VÉRIFIE CES POINTS</SectionTitle>

                    {isSoundMode === false && (
                        <Step
                            number="1"
                            text="Vérifie que tu es en mode Dialogue"
                            subText="Mode actuel : Concentré (son coupé)"
                        />
                    )}
                    {IS_WEB ? (
                        <>
                            <Step
                                number={isSoundMode === false ? "2" : "1"}
                                text="Certains claviers ont une touche qui coupe le micro"
                                subText="Vérifie le bouton mute sur ton clavier (souvent en haut à droite)"
                            />

                            {/* Keyboard mute image */}
                            <View
                                style={{
                                    backgroundColor: theme.colors.lighter,
                                    padding: 10,
                                    borderRadius: 10,
                                    marginBottom: 12,
                                }}
                            >
                                <Image
                                    source={micMutePc}
                                    style={{
                                        width: "100%",
                                        height: 100,
                                        borderRadius: 8,
                                    }}
                                    resizeMode="contain"
                                />
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.grey}
                                    style={{ marginTop: 6, textAlign: "center" }}
                                >
                                    Exemple de touche micro (si ton clavier en a une)
                                </TextM>
                            </View>
                        </>
                    ) : (
                        <>
                            <Step
                                number={isSoundMode === false ? "2" : "1"}
                                text="Ferme et rouvre l'application"
                                subText="Cela permet de relancer le micro"
                            />
                            <Step
                                number={isSoundMode === false ? "3" : "2"}
                                text="Essaie de brancher ou débrancher des écouteurs"
                                subText="Cela peut changer le micro utilisé"
                            />
                            <Step
                                number={isSoundMode === false ? "4" : "3"}
                                text="Ferme les autres applis qui utilisent le micro"
                                subText="Snap, Teams, Zoom, FaceTime..."
                            />
                        </>
                    )}

                    <View
                        style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}
                    >
                        {IS_WEB ? (
                            <QuickAction
                                icon="redo"
                                label="Rafraîchir la page"
                                onPress={() => window.location.reload()}
                            />
                        ) : (
                            <QuickAction
                                icon="cog"
                                label="Ouvrir les paramètres"
                                onPress={() => Linking.openSettings()}
                            />
                        )}
                        <DeviceChangeButton kind="audioinput" isSoundMode={isSoundMode} />
                    </View>
                </>
            )}

            <EscalationSection
                onRaiseHand={isSoundMode === false ? null : onRaiseHand}
                onOpenChat={isSoundMode === false ? null : onOpenChat}
                onOpenReport={onOpenReport}
            />
        </View>
    )
}

const AudioDiagnostic = ({
    isSoundMode,
    onRaiseHand,
    onOpenChat,
    onOpenReport,
    role,
}) => (
    <View>
        {isSoundMode === false && (
            <>
                <StatusBanner
                    icon="volume-mute"
                    text={
                        role === "student"
                            ? "Tu es en mode Concentré — le son est coupé, c'est normal. Lève la main ou écris un message pour demander au tuteur de te remettre en mode Dialogue."
                            : "Vérifiez que les élèves sont en mode Dialogue."
                    }
                    color={theme.colors.purple}
                />
                {role === "student" && (
                    <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                        {onRaiseHand && (
                            <QuickAction
                                icon="hand-paper"
                                label="Lever la main"
                                onPress={onRaiseHand}
                            />
                        )}
                        {onOpenChat && (
                            <QuickAction
                                icon="comment"
                                label="Écrire au tuteur"
                                onPress={onOpenChat}
                            />
                        )}
                    </View>
                )}
            </>
        )}

        <SectionTitle>ÉTAPES À SUIVRE</SectionTitle>

        {role === "student" && (
            <Step
                number="1"
                text="Vérifie que tu es en mode Dialogue"
                subText={
                    isSoundMode === false
                        ? "Mode actuel : Concentré (son coupé)"
                        : "Mode actuel : Dialogue (son activé)"
                }
            />
        )}
        <Step
            number={role === "student" ? "2" : "1"}
            text="Monte le volume de ton appareil"
        />
        <Step
            number={role === "student" ? "3" : "2"}
            text={
                IS_WEB
                    ? "Vérifie le bon haut-parleur"
                    : "Essaie de brancher ou débrancher des écouteurs"
            }
            subText={
                IS_WEB
                    ? "Si tu as un casque, vérifie qu'il est bien branché"
                    : "Cela peut changer le haut-parleur utilisé"
            }
        />
        {IS_WEB ? (
            <>
                <Step
                    number={role === "student" ? "4" : "3"}
                    text="Change de haut-parleur ou de micro via la flèche sur le bouton micro"
                    subText="Il se peut que le mauvais appareil soit sélectionné"
                />
                <Step
                    number={role === "student" ? "5" : "4"}
                    text="Rafraîchis la page"
                    subText="Parfois un simple rechargement résout le problème"
                />
            </>
        ) : (
            <Step
                number={role === "student" ? "4" : "3"}
                text="Ferme et rouvre l'application"
                subText="Parfois un simple redémarrage résout le problème"
            />
        )}

        <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
            {IS_WEB ? (
                <>
                    <DeviceChangeButton kind="audioinput" isSoundMode={isSoundMode} />
                    <QuickAction
                        icon="redo"
                        label="Rafraîchir la page"
                        onPress={() => window.location.reload()}
                    />
                </>
            ) : (
                <QuickAction
                    icon="cog"
                    label="Ouvrir les paramètres"
                    onPress={() => Linking.openSettings()}
                />
            )}
        </View>

        <EscalationSection
            onRaiseHand={isSoundMode === false ? null : onRaiseHand}
            onOpenChat={isSoundMode === false ? null : onOpenChat}
            onOpenReport={onOpenReport}
        />
    </View>
)

const CameraDiagnostic = ({
    isSoundMode,
    onRaiseHand,
    onOpenChat,
    onOpenReport,
    parentPermissions,
    parentRequestPermissions,
}) => {
    const ownPermissions = usePermissions()
    const permissionsGranted = parentPermissions || ownPermissions.permissionsGranted
    const requestPermissions = parentRequestPermissions || ownPermissions.requestPermissions
    const { isCameraEnabled } = useLocalParticipant()

    const isCamPermissionOk = permissionsGranted?.camera
    const isCamEnabled = isCameraEnabled

    return (
        <View>
            {/* Auto-diagnostic result */}
            {isCamPermissionOk && isCamEnabled !== false ? (
                <StatusBanner
                    icon="check-circle"
                    text="Ta caméra semble fonctionner. Si le problème persiste, suis les étapes ci-dessous."
                    color={theme.colors.success}
                />
            ) : isCamPermissionOk && isCamEnabled === false ? (
                <StatusBanner
                    icon="video-slash"
                    text="Ta caméra est désactivée. Clique sur le bouton caméra en bas de l'écran pour la réactiver."
                    color={theme.colors.warning}
                />
            ) : (
                <>
                    <StatusBanner
                        icon="shield-alt"
                        text={
                            IS_WEB
                                ? "La caméra n'est pas autorisée dans ton navigateur. Clique sur le bouton à gauche de la barre d'adresse, autorise la caméra, puis rafraîchis la page."
                                : "La caméra n'est pas autorisée. Ouvre les paramètres de l'application pour l'activer."
                        }
                        color={theme.colors.error}
                    />
                    {IS_WEB && (
                        <Image
                            source={guidancePermissionWeb}
                            style={{ width: "100%", height: 40, borderRadius: 8, marginBottom: 12 }}
                            resizeMode="contain"
                        />
                    )}
                </>
            )}

            {/* When permissions denied: show only permission fix flow */}
            {!isCamPermissionOk && (
                <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <PermissionAction
                        kind="camera"
                        onRequestPermission={requestPermissions}
                    />
                </View>
            )}

            {/* When permissions OK: show troubleshooting steps */}
            {isCamPermissionOk && (
                <>
                    <SectionTitle>ÉTAPES SI ÇA NE MARCHE PAS</SectionTitle>

                    <Step
                        number="1"
                        text="Vérifie que ta caméra n'est pas couverte"
                        subText="Certains ordinateurs ont un cache physique"
                    />
                    <Step
                        number="2"
                        text="Ferme les applis qui utilisent la caméra"
                        subText="Snap, Teams, Zoom, FaceTime..."
                    />
                    {!IS_WEB && (
                        <Step
                            number="3"
                            text="Ferme et rouvre l'application"
                            subText="Cela permet de relancer la caméra"
                        />
                    )}

                    <View
                        style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}
                    >
                        {IS_WEB ? (
                            <QuickAction
                                icon="redo"
                                label="Rafraîchir la page"
                                onPress={() => window.location.reload()}
                            />
                        ) : (
                            <QuickAction
                                icon="cog"
                                label="Ouvrir les paramètres"
                                onPress={() => Linking.openSettings()}
                            />
                        )}
                        <DeviceChangeButton kind="videoinput" isSoundMode={isSoundMode} />
                    </View>
                </>
            )}

            <EscalationSection
                onRaiseHand={onRaiseHand}
                onOpenChat={onOpenChat}
                onOpenReport={onOpenReport}
            />
        </View>
    )
}

const ConnectionDiagnostic = ({ onOpenChat, onOpenReport }) => {
    const { localParticipant } = useLocalParticipant()
    const { quality } = useConnectionQualityIndicator({
        participant: localParticipant,
    })

    const connectionInfo =
        CONNECTION_LABELS[quality] ||
        CONNECTION_LABELS[ConnectionQuality.Unknown]
    const isConnectionOk = [
        ConnectionQuality.Excellent,
        ConnectionQuality.Good,
    ].includes(quality)

    return (
        <View>
            {/* Quality badge */}
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                    backgroundColor: connectionInfo.color + "15",
                    padding: 10,
                    borderRadius: 8,
                }}
            >
                <FontAwesome5
                    name="signal"
                    size={14}
                    color={connectionInfo.color}
                />
                <TextM
                    fontWeight="SemiBold"
                    fontSize={13}
                    color={connectionInfo.color}
                >
                    Qualité de connexion : {connectionInfo.label}
                </TextM>
            </View>

            {isConnectionOk ? (
                <StatusBanner
                    icon="check-circle"
                    text="Ta connexion semble bonne. Si le problème persiste, suis les recommandations ci-dessous."
                    color={theme.colors.success}
                />
            ) : (
                <StatusBanner
                    icon="exclamation-triangle"
                    text="Ta connexion est instable. Suis les recommandations ci-dessous pour l'améliorer."
                    color={theme.colors.warning}
                />
            )}

            <SectionTitle>RECOMMANDATIONS</SectionTitle>

            <Step
                number="1"
                text="Rapproche-toi de ta box Wi-Fi"
                subText="Plus tu es proche, meilleur est le signal"
            />
            <Step
                number="2"
                text="Ferme les autres applis et onglets"
                subText="Jeux, YouTube, réseaux sociaux..."
            />
            <Step
                number="3"
                text={
                    IS_WEB
                        ? "Branche-toi en câble si possible"
                        : "Passe en données mobiles (4G/5G)"
                }
                subText={
                    IS_WEB
                        ? "Le câble Ethernet est plus stable que le Wi-Fi"
                        : "Si le Wi-Fi est mauvais, les données mobiles peuvent être plus stables"
                }
            />
            {!IS_WEB && (
                <Step
                    number="4"
                    text="Ferme et rouvre l'application"
                    subText="Cela permet de relancer la connexion"
                />
            )}

            <View
                style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}
            >
                {IS_WEB ? (
                    <QuickAction
                        icon="redo"
                        label="Rafraîchir la page"
                        onPress={() => window.location.reload()}
                    />
                ) : (
                    <QuickAction
                        icon="cog"
                        label="Ouvrir les paramètres"
                        onPress={() => Linking.openSettings()}
                    />
                )}
            </View>

            {/* Connection issues → chat only (voice might not work) */}
            <EscalationSection
                onOpenChat={onOpenChat}
                onOpenReport={onOpenReport}
                showRaiseHand={false}
            />
        </View>
    )
}

const OtherDiagnostic = ({ onRaiseHand, onOpenChat, onOpenReport, role }) => (
    <View>
        <TextM
            fontSize={13}
            color={theme.colors.grey}
            style={{ marginBottom: 16 }}
            wrap
        >
            Voici les problèmes les plus courants et comment les résoudre :
        </TextM>

        <SectionTitle>ÉCRAN NOIR OU PAGE BLANCHE</SectionTitle>
        <Step
            number="1"
            text={IS_WEB ? "Rafraîchis la page" : "Ferme et rouvre l'application"}
            subText={IS_WEB ? "Ctrl+R ou Cmd+R" : undefined}
        />
        <Step number="2" text="Vérifie ta connexion Internet" />

        <SectionTitle>ACTIVITÉ QUI NE CHARGE PAS</SectionTitle>
        <Step
            number="1"
            text="Attends quelques secondes"
            subText="Le chargement peut prendre un moment"
        />
        <Step
            number="2"
            text={IS_WEB ? "Rafraîchis la page si rien ne se passe" : "Ferme et rouvre l'application si rien ne se passe"}
        />

        <SectionTitle>JE NE VOIS PAS LE PARTAGE D'ÉCRAN</SectionTitle>
        <Step
            number="1"
            text={
                role === "student"
                    ? "Vérifie que ton tuteur a lancé le partage d'écran"
                    : "Vérifie que le partage d'écran est activé"
            }
        />
        <Step
            number="2"
            text={IS_WEB ? "Rafraîchis la page" : "Ferme et rouvre l'application"}
        />

        {IS_WEB && (
            <View style={{ flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
                <QuickAction
                    icon="redo"
                    label="Rafraîchir la page"
                    onPress={() => window.location.reload()}
                />
            </View>
        )}

        <EscalationSection
            onRaiseHand={onRaiseHand}
            onOpenChat={onOpenChat}
            onOpenReport={onOpenReport}
        />
    </View>
)

// ---------------------------------------------------------------------------
//  Main component
// ---------------------------------------------------------------------------

const TroubleshootingPanel = ({
    visible,
    onDismiss,
    isSoundMode,
    onRaiseHand,
    onOpenChat,
    onOpenReport,
    role = "student",
    initialProblem = null,
    micLevel: parentMicLevel,
    permissionsGranted: parentPermissions,
    requestPermissions: parentRequestPermissions,
}) => {
    const [selectedProblem, setSelectedProblem] = useState(null)

    // Reset or set initial problem when panel opens/closes
    useEffect(() => {
        if (visible) {
            setSelectedProblem(initialProblem || null)
            amplitude.track("troubleshooting.panel.opened")
        }
    }, [visible, initialProblem])

    const handleDismiss = () => {
        setSelectedProblem(null)
        onDismiss()
    }

    const handleBack = () => setSelectedProblem(null)

    const problemOption = PROBLEM_OPTIONS.find(
        (o) => o.key === selectedProblem
    )
    const problemTitle = problemOption
        ? role === "tutor" && problemOption.tutorLabel
            ? problemOption.tutorLabel
            : problemOption.label
        : null

    const sharedProps = {
        isSoundMode,
        onRaiseHand,
        onOpenChat,
        onOpenReport,
        role,
        parentMicLevel,
        parentPermissions,
        parentRequestPermissions,
    }

    const renderDiagnostic = () => {
        switch (selectedProblem) {
            case PROBLEMS.MIC_NOT_WORKING:
                return <MicDiagnostic {...sharedProps} />
            case PROBLEMS.CANT_HEAR:
                return <AudioDiagnostic {...sharedProps} />
            case PROBLEMS.CAMERA_NOT_WORKING:
                return <CameraDiagnostic {...sharedProps} />
            case PROBLEMS.CONNECTION_ISSUES:
                return <ConnectionDiagnostic {...sharedProps} />
            case PROBLEMS.OTHER:
                return <OtherDiagnostic {...sharedProps} />
            default:
                return null
        }
    }

    return (
        <ModalM
            visible={visible}
            onDismiss={handleDismiss}
            style={{ maxHeight: "90%", maxWidth: 500, width: "95%" }}
        >
            <View style={{ maxHeight: "100%" }}>
                {/* Header */}
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingHorizontal: 16,
                        paddingTop: 16,
                        paddingBottom: 8,
                    }}
                >
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 4,
                            flex: 1,
                        }}
                    >
                        {selectedProblem && (
                            <IconButtonM
                                icon="arrow-left"
                                size={18}
                                onPress={handleBack}
                                iconColor={theme.colors.primary}
                                style={{ margin: 0 }}
                            />
                        )}
                        <TitleM fontWeight="ExtraBold" style={{ flex: 1 }}>
                            {selectedProblem
                                ? problemTitle
                                : "Un problème ?"}
                        </TitleM>
                    </View>
                    <IconButtonM
                        icon="times"
                        size={20}
                        onPress={handleDismiss}
                        iconColor={theme.colors.primary}
                    />
                </View>

                {/* Content — only render when visible to avoid hooks running in background */}
                {visible && (
                    <ScrollView
                        style={{ maxHeight: "100%" }}
                        contentContainerStyle={{
                            paddingHorizontal: 16,
                            paddingBottom: 24,
                        }}
                        showsVerticalScrollIndicator={false}
                    >
                        {selectedProblem ? (
                            renderDiagnostic()
                        ) : (
                            <ProblemSelector
                                onSelectProblem={(problem) => {
                                    amplitude.track(
                                        "troubleshooting.problem.selected"
                                    )
                                    setSelectedProblem(problem)
                                }}
                                role={role}
                            />
                        )}
                    </ScrollView>
                )}
            </View>
        </ModalM>
    )
}

export default TroubleshootingPanel
